import {
  NotificationType,
  PublishDiagnosticsParams,
  DidChangeWorkspaceFoldersNotification,
  CompletionItem,
  InlineCompletionRequest,
  Disposable,
} from 'vscode-languageserver-protocol';
import { createInterfaceId, Injectable } from '@gitlab-org/di';
import { ThemeNotificationHandler } from '@gitlab-org/webview-theme';
import { InitializeHandler } from './core/handlers/initialize_handler';
import { DidChangeWorkspaceFoldersHandler } from './core/handlers/did_change_workspace_folders_handler';
import { TokenCheckNotifier } from './core/handlers/token_check_notifier';
import { Notifier } from './notifier';
import { LsConnection } from './external_interfaces';
import { SecurityDiagnosticsPublisher } from './security_diagnostics_publisher';
import { DiagnosticsPublisher } from './diagnostics_publisher';
import { FeatureStateManager } from './feature_state';
import {
  FeatureStateChangeNotificationType,
  TokenCheckNotificationType,
  DidChangeDocumentInActiveEditor,
  DidChangeThemeNotificationType,
  ApiErrorNotificationType,
  ApiRecoveryNotificationType,
  StreamingCompletionResponseNotificationType,
  CancelStreamingNotificationType,
} from './notifications';
import { DocumentService } from './document_service';
import { SuggestionApiErrorNotifier } from './feature_state/suggestion_api_error_notifier';
import { StreamingHandler } from './suggestion/streaming_handler';
import { SuggestionService } from './suggestion/suggestion_service';
import { log } from './log';
import { AIContextManager } from './ai_context_management/ai_context_manager';
import { FeatureFlagService, InstanceFeatureFlags } from './feature_flags';
import { AIContextEndpoints, AIContextEndpointTypes, AIContextItem } from './ai_context_management';

export interface ConnectionService {}

export const ConnectionService = createInterfaceId<ConnectionService>('ConnectionService');

const createNotifyFn =
  <T>(c: LsConnection, method: NotificationType<T>) =>
  (param: T) =>
    c.sendNotification(method, param);

const createDiagnosticsPublisherFn = (c: LsConnection) => (param: PublishDiagnosticsParams) =>
  c.sendDiagnostics(param);

@Injectable(ConnectionService, [
  LsConnection,
  TokenCheckNotifier,
  InitializeHandler,
  DidChangeWorkspaceFoldersHandler,
  SecurityDiagnosticsPublisher,
  DocumentService,
  FeatureStateManager,
  ThemeNotificationHandler,
  SuggestionApiErrorNotifier,
  StreamingHandler,
  SuggestionService,
  AIContextManager,
  FeatureFlagService,
])
export class DefaultConnectionService implements ConnectionService {
  #connection: LsConnection;

  #featureFlagService: FeatureFlagService;

  constructor(
    connection: LsConnection,
    tokenCheckNotifier: TokenCheckNotifier,
    initializeHandler: InitializeHandler,
    didChangeWorkspaceFoldersHandler: DidChangeWorkspaceFoldersHandler,
    securityDiagnosticsPublisher: SecurityDiagnosticsPublisher,
    documentService: DocumentService,
    featureStateManager: FeatureStateManager,
    themeNotificationHandler: ThemeNotificationHandler,
    suggestionApiErrorNotifier: SuggestionApiErrorNotifier,
    streamingHandler: StreamingHandler,
    suggestionService: SuggestionService,
    aiContextManager: AIContextManager,
    featureFlagService: FeatureFlagService,
  ) {
    this.#connection = connection;
    this.#featureFlagService = featureFlagService;

    // request handlers
    connection.onInitialize(initializeHandler.requestHandler);
    // suggestion handlers
    connection.onCompletion(suggestionService.completionHandler);
    // TODO: does Visual Studio or Neovim need this? VS Code doesn't
    connection.onCompletionResolve((item: CompletionItem) => item);
    connection.onRequest(InlineCompletionRequest.type, suggestionService.inlineCompletionHandler);

    connection.onInitialized(() => {
      // notifiers
      this.#initializeNotifier(TokenCheckNotificationType, tokenCheckNotifier);
      this.#initializeNotifier(FeatureStateChangeNotificationType, featureStateManager);
      this.#initializeNotifier(StreamingCompletionResponseNotificationType, streamingHandler);

      suggestionApiErrorNotifier.setErrorNotifyFn(
        createNotifyFn(this.#connection, ApiErrorNotificationType),
      );
      suggestionApiErrorNotifier.setRecoveryNotifyFn(
        createNotifyFn(this.#connection, ApiRecoveryNotificationType),
      );
    });

    // notification handlers
    connection.onNotification(DidChangeDocumentInActiveEditor, documentService.notificationHandler);
    connection.onNotification(
      DidChangeWorkspaceFoldersNotification.method,
      didChangeWorkspaceFoldersHandler.notificationHandler,
    );
    connection.onNotification(
      DidChangeThemeNotificationType,
      themeNotificationHandler.handleThemeChange,
    );
    connection.onNotification(
      CancelStreamingNotificationType,
      streamingHandler.notificationHandler,
    );

    // diagnostics publishers
    this.#initializeDiagnosticsPublisher(securityDiagnosticsPublisher);

    // AI Context
    this.#setupAiContextHandlers(aiContextManager);
  }

  #initializeNotifier<T>(method: NotificationType<T>, notifier: Notifier<T>) {
    notifier.init(createNotifyFn(this.#connection, method));
  }

  #initializeDiagnosticsPublisher(publisher: DiagnosticsPublisher) {
    publisher.init(createDiagnosticsPublisherFn(this.#connection));
  }

  #onAIContextRequest<K extends keyof AIContextEndpointTypes>(
    method: K,
    handler: (
      params: AIContextEndpointTypes[K]['request'],
    ) => Promise<AIContextEndpointTypes[K]['response']>,
  ): Disposable {
    return this.#connection.onRequest(method, handler);
  }

  #setupAiContextHandlers(aiContextManager: AIContextManager) {
    this.#onAIContextRequest(
      AIContextEndpoints.QUERY,
      async ({ category, query, workspaceFolders }) => {
        log.info(`Advanced Context: recieved search query ${JSON.stringify(category)}`);
        let results: AIContextItem[] = [];
        try {
          results = await aiContextManager.searchContextItemsForCategory({
            category,
            query,
            workspaceFolders,
          });
        } catch (e) {
          log.error(`Advanced Context: error searching context items`, e);
        }
        log.info(`Advanced Context: search query had ${results.length} results`);

        return results;
      },
    );

    this.#onAIContextRequest(AIContextEndpoints.ADD, async (item) => {
      // FIXME: aiContextManager should return the result of the operation upstream,
      // the methods should also be async https://gitlab.com/gitlab-org/gitlab/-/issues/489292
      try {
        log.info(`Advanced Context: recieved context item add request ${item.id}`);
        aiContextManager.addSelectedContextItem(item);
        log.info(`Advanced Context: added item result ${true}`);
        return true;
      } catch (e) {
        log.error(`Advanced Context: error adding context item`, e);
        return false;
      }
    });

    this.#onAIContextRequest(AIContextEndpoints.REMOVE, async (item: AIContextItem) => {
      log.info(`Advanced Context: recieved context item remove request ${item.id}`);
      try {
        aiContextManager.removeSelectedContextItem(item);
        log.info(`Advanced Context: removed item result ${true}`);
        return true;
      } catch (e) {
        log.error(`Advanced Context: error removing context item`, e);
        return false;
      }
    });

    this.#onAIContextRequest(AIContextEndpoints.CURRENT_ITEMS, async () => {
      const currentItems = await aiContextManager.getSelectedContextItems();
      log.info(`Advanced Context: returning ${currentItems.length} current items`);
      return currentItems;
    });

    this.#onAIContextRequest(AIContextEndpoints.RETRIEVE, async () => {
      const items = await aiContextManager.retrieveSelectedContextItemsWithContent();
      log.info(`Advanced Context: retrieved ${items.length} current items`);
      return items;
    });

    this.#onAIContextRequest(AIContextEndpoints.GET_PROVIDER_CATEGORIES, async () => {
      if (
        !this.#featureFlagService.isInstanceFlagEnabled(InstanceFeatureFlags.DuoAdditionalContext)
      )
        return [];
      const currentItems = await aiContextManager.getAvailableCategories();
      log.info(`Advanced Context: returning ${currentItems.length} available providers`);
      return currentItems;
    });

    this.#onAIContextRequest(AIContextEndpoints.CLEAR, async () => {
      log.info(`Advanced Context: recieved clear request`);
      aiContextManager.clearSelectedContextItems();
      return true;
    });
  }
}
